import { useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  COLUMN_GAP,
  COLUMN_WIDTH,
} from "@miplatform/constants";
import { getContrastColor, toKebabCase } from "@miplatform/utils";
import {
  mdiChevronDown,
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronUp,
} from "@mdi/js";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import { MiButton } from "../../MiButton/MiButton";
import { MiIcon } from "../../MiIcon/MiIcon";

const SCROLL_UNIT = COLUMN_WIDTH + 2;

/**
 * Bucket component displays a collapsible section with a header and horizontally scrollable content.
 * It shows a count of items, supports custom header/content colors, and provides left/right scroll buttons
 * when the content overflows horizontally.
 *
 * @param {Object} props - The component props.
 * @param {Object} [props.bucket] - The bucket data and configuration.
 * @param {Array} [props.bucket.data] - The array of items to display in the bucket.
 * @param {string} [props.bucket.name] - The name of the bucket, displayed in the header.
 * @param {boolean} [props.bucket.collapsible] - Whether the bucket is collapsible.
 * @param {string} [props.bucket.headerBackgroundColor] - Optional custom header background color.
 * @param {string} [props.bucket.headerColor] - Optional custom header text color.
 * @param {string} [props.bucket.contentBackgroundColor] - Optional custom content background color.
 * @param {string} [props.bucket.countBackgroundColor] - Optional custom count badge background color.
 * @param {string} [props.bucket.countColor] - Optional custom count badge text color.
 * @param {boolean} [props.initialCollapsed] - Optional initial collapsed state.
 * @param {React.ReactNode} [props.headerEndContent] - Optional content rendered in the header (e.g. action buttons).
 * @param {React.ReactNode} props.children - The content to render inside the scrollable area.
 *
 * @returns {JSX.Element} The rendered Bucket component.
 */
export function Bucket({ bucket, children, headerEndContent, ...props }) {
  const isBucketEmpty = bucket.data?.length === 0;
  const [collapse, setCollapse] = useState(
    props.initialCollapsed || (bucket.collapsible && isBucketEmpty)
  );
  const scrollRef = useRef(null);
  const { classes } = useStyles({ bucket });
  const hideBucket =
    (bucket.hideWhenEmpty && !bucket.data?.length) || !bucket.isActive;

  const toggleCollapse = () => {
    if (bucket.collapsible) {
      setCollapse((prev) => !prev);
    }
  };

  useEffect(() => {
    if (bucket.collapsible) {
      if (isBucketEmpty) {
        setCollapse(true);
      } else setCollapse(false);
    }
  }, [isBucketEmpty, bucket.collapsible]);

  const getIsBucketScrollable = () => {
    return scrollRef.current?.scrollWidth > scrollRef.current?.clientWidth;
  };

  const getLeftScrollStatus = () => {
    return getIsBucketScrollable() && scrollRef.current?.scrollLeft > 0;
  };

  const getRightScrollStatus = () => {
    return (
      getIsBucketScrollable() &&
      scrollRef.current &&
      scrollRef.current.scrollLeft <
        scrollRef.current.scrollWidth - scrollRef.current.clientWidth
    );
  };

  const [enableLeftScrollButton, setEnableLeftScrollButton] = useState(
    getLeftScrollStatus()
  );
  const [enableRightScrollButton, setEnableRightScrollButton] = useState(
    getRightScrollStatus()
  );

  const updateScrollButtons = () => {
    setEnableLeftScrollButton(getLeftScrollStatus());
    setEnableRightScrollButton(getRightScrollStatus());
  };

  useEffect(() => {
    updateScrollButtons();
  }, [bucket.collapsible, bucket.data?.length]);

  useEffect(() => {
    scrollRef.current?.addEventListener("scroll", updateScrollButtons);
    return () => {
      scrollRef.current?.removeEventListener("scroll", updateScrollButtons);
    };
  }, []);

  const theme = useTheme();

  return (
    <Box
      overflow="hidden"
      width="100%"
      data-testid={`${toKebabCase(bucket.name)}-bucket`}
    >
      {hideBucket ? null : (
        <>
          <Box
            data-testid="header"
            onClick={toggleCollapse}
            className={classes.drawerHeader}
          >
            <Box className={classes.statusContainer}>
              <Box
                className={classes.countContainer}
                data-testid="counter-container"
              >
                <Typography
                  className={classes.totalCountStyles}
                  aria-hidden="true"
                >
                  {bucket.data?.length || 0}
                </Typography>
              </Box>
              <Typography fontSize={theme.fontSize.small}>
                {bucket.name}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={theme.spacing(1)}>
              {headerEndContent ? (
                <Box onClick={(e) => e.stopPropagation()}>
                  {headerEndContent}
                </Box>
              ) : null}
              {bucket?.collapsible ? (
                <MiIcon
                  path={collapse ? mdiChevronUp : mdiChevronDown}
                  color={
                    bucket.headerColor ??
                    (bucket.headerBackgroundColor
                      ? getContrastColor(bucket.headerBackgroundColor)
                      : theme.palette.primary.main)
                  }
                  data-testid="collapsible-icon"
                  size={0.8}
                />
              ) : null}
            </Box>
          </Box>
          <Collapse in={!collapse} timeout="auto">
            <Box className={classes.drawerContent}>
              <MiButton
                variant={BUTTON_VARIANTS.CONTAINED}
                size="medium"
                data-testid="left-icon"
                className={classes.scrollButton}
                disabled={!enableLeftScrollButton}
                style={{
                  border: enableLeftScrollButton
                    ? `1px solid ${theme.palette.orange.main}`
                    : "none",
                }}
                color={BUTTON_COLORS.WHITE}
                startIcon={{
                  path: mdiChevronLeft,
                  iconColor: enableLeftScrollButton
                    ? theme.palette.primary.main
                    : theme.palette.primary.grey,
                  iconSize: 0.8,
                }}
                onClick={() => {
                  scrollRef.current.scrollTo({
                    left: scrollRef.current.scrollLeft - SCROLL_UNIT,
                    behavior: "smooth",
                  });
                }}
              />
              <Box className={classes.scrollContainer} ref={scrollRef}>
                {children}
              </Box>
              <MiButton
                variant={BUTTON_VARIANTS.CONTAINED}
                size="medium"
                data-testid="right-icon"
                className={classes.scrollButton}
                disabled={!enableRightScrollButton}
                style={{
                  border: enableRightScrollButton
                    ? `1px solid ${theme.palette.orange.main}`
                    : "none",
                }}
                color={BUTTON_COLORS.WHITE}
                startIcon={{
                  path: mdiChevronRight,
                  iconColor: enableRightScrollButton
                    ? theme.palette.primary.main
                    : theme.palette.primary.grey,
                  iconSize: 0.8,
                }}
                onClick={() => {
                  scrollRef.current.scrollTo({
                    left: scrollRef.current.scrollLeft + SCROLL_UNIT,
                    behavior: "smooth",
                  });
                }}
              />
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
}
const useStyles = makeStyles()((theme, { bucket }) => ({
  drawerHeader: {
    justifyContent: "space-between",
    display: "flex",
    backgroundColor: bucket.headerBackgroundColor ?? "inherit",
    color: bucket.headerColor ?? "inherit",
    padding: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
  },
  drawerContent: {
    display: "flex",
    overflow: "hidden",
    minHeight: 61,
    backgroundColor: bucket.contentBackgroundColor ?? theme.palette.blue.light,
  },
  scrollContainer: {
    display: "flex",
    boxSizing: "content-box",
    flex: 1,
    gap: COLUMN_GAP,
    "&::-webkit-scrollbar": { display: "none" } /* webkit */,
    padding: theme.spacing(1, 0),
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,
    overflowX: "scroll",
  },
  scrollButton: {
    padding: theme.spacing(0.5),
    margin: theme.spacing(0.5),
    borderRadius: "10%",
    backgroundColor: theme.palette.common.white,
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
  },
  countContainer: {
    height: 14,
    width: 14,
    background: theme.palette.blue.main,
    borderRadius: "100%",
    marginRight: 10,
    marginLeft: 10,
  },
  totalCountStyles: {
    marginRight: 10,
    backgroundColor: bucket.countBackgroundColor ?? theme.palette.red.medium,
    color: bucket.countColor ?? theme.palette.common.white,
    borderRadius: "100%",
    height: 14,
    width: 14,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: theme.fontSize.xxsmall,
  },
  disabled: {
    padding: theme.spacing(0.5),
    color: theme.palette.grey.main,
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
  },
}));
